import { useCallback } from 'react';
import {
  roundToPrecision,
  parseFloatWithPrecision,
} from '../utils/timelineUtils';

export const useAnimationSettings = (state, setState) => {
  const {
    selectedEffects,
    setSelectedEffects,
    timelineEffects,
    setTimelineEffects,
    animationSettings,
    setAnimationSettings,
  } = state;

  const {
    setSelectedEffects: setSelectedEffectsState,
    setTimelineEffects: setTimelineEffectsState,
    setAnimationSettings: setAnimationSettingsState,
  } = setState;

  // Load settings from selected effect
  const loadSettingsFromSelectedEffect = useCallback(() => {
    if (selectedEffects.length === 1) {
      const selectedEffect = timelineEffects.find(
        (effect) => effect.id === selectedEffects[0]
      );
      if (selectedEffect) {
        setAnimationSettingsState({
          duration:
            selectedEffect.duration ||
            selectedEffect.endTime - selectedEffect.startTime,
          startTime: selectedEffect.startTime,
          easing: selectedEffect.easing || 'None',
          activeAxes: selectedEffect.activeAxes || {
            x: true,
            y: false,
            z: true,
          },
          startPosition: selectedEffect.startPosition || { x: 1, y: 0, z: 0 },
          endPosition: selectedEffect.endPosition || { x: 10, y: 0, z: 0 },
          loop: selectedEffect.loop || false,
        });
      }
    }
  }, [selectedEffects, timelineEffects, setAnimationSettingsState]);

  // Apply settings to selected effects
  const applySettingsToSelectedEffects = useCallback(
    (settingKey, value) => {
      if (selectedEffects.length > 0) {
        setTimelineEffectsState((prev) =>
          prev.map((effect) => {
            if (selectedEffects.includes(effect.id)) {
              if (settingKey === 'duration') {
                const newEndTime = effect.startTime + value;

                // Check for overlaps when duration changes and handle conflicts
                const conflictingEffects = prev.filter(
                  (e) =>
                    e.objectId === effect.objectId &&
                    e.id !== effect.id &&
                    (e.row || 0) === (effect.row || 0) &&
                    !(
                      newEndTime <= e.startTime || effect.startTime >= e.endTime
                    )
                );

                // Move conflicting effects to next available rows
                if (conflictingEffects.length > 0) {
                  conflictingEffects.forEach((conflictingEffect) => {
                    const nextRow = findNextAvailableRow(
                      effect.objectId,
                      conflictingEffect.startTime,
                      conflictingEffect.endTime,
                      prev,
                      conflictingEffect.id
                    );
                    setTimelineEffectsState((current) =>
                      current.map((e) =>
                        e.id === conflictingEffect.id
                          ? { ...e, row: nextRow }
                          : e
                      )
                    );
                  });
                }

                return {
                  ...effect,
                  duration: value,
                  endTime: newEndTime,
                };
              } else if (settingKey === 'startTime') {
                const duration = effect.endTime - effect.startTime;
                const newEndTime = value + duration;

                // Check for overlaps when start time changes and handle conflicts
                const conflictingEffects = prev.filter(
                  (e) =>
                    e.objectId === effect.objectId &&
                    e.id !== effect.id &&
                    (e.row || 0) === (effect.row || 0) &&
                    !(newEndTime <= e.startTime || value >= e.endTime)
                );

                // Move conflicting effects to next available rows
                if (conflictingEffects.length > 0) {
                  conflictingEffects.forEach((conflictingEffect) => {
                    const nextRow = findNextAvailableRow(
                      effect.objectId,
                      conflictingEffect.startTime,
                      conflictingEffect.endTime,
                      prev,
                      conflictingEffect.id
                    );
                    setTimelineEffectsState((current) =>
                      current.map((e) =>
                        e.id === conflictingEffect.id
                          ? { ...e, row: nextRow }
                          : e
                      )
                    );
                  });
                }

                return {
                  ...effect,
                  startTime: value,
                  endTime: newEndTime,
                };
              } else {
                return {
                  ...effect,
                  [settingKey]: value,
                };
              }
            }
            return effect;
          })
        );
      }
    },
    [selectedEffects, setTimelineEffectsState]
  );

  const updateSelectedEffectsProperty = useCallback(
    (key, value) => {
      if (selectedEffects.length > 0) {
        setTimelineEffectsState((prev) =>
          prev.map((effect) =>
            selectedEffects.includes(effect.id)
              ? {
                  ...effect,
                  [key]: value,
                }
              : effect
          )
        );
      }
    },
    [selectedEffects, setTimelineEffectsState]
  );

  // Update setting
  const updateSetting = useCallback(
    (key, value) => {
      const validValue =
        typeof value === 'number'
          ? Math.max(0, roundToPrecision(value, 1))
          : value;
      setAnimationSettingsState((prev) => ({
        ...prev,
        [key]: validValue,
      }));

      // Apply to selected effects
      applySettingsToSelectedEffects(key, validValue);
    },
    [setAnimationSettingsState, applySettingsToSelectedEffects, selectedEffects]
  );

  // Update position
  const updatePosition = useCallback(
    (positionType, axis, value) => {
      const numValue = parseFloat(value) || 0;
      const newPosition = {
        ...animationSettings[positionType],
        [axis]: numValue,
      };

      setAnimationSettingsState((prev) => ({
        ...prev,
        [positionType]: newPosition,
      }));

      // Apply to selected effects
      applySettingsToSelectedEffects(positionType, newPosition);
    },
    [
      animationSettings,
      setAnimationSettingsState,
      applySettingsToSelectedEffects,
    ]
  );

  // Toggle axis
  const toggleAxis = useCallback(
    (axis) => {
      const newActiveAxes = {
        ...animationSettings.activeAxes,
        [axis]: !animationSettings.activeAxes[axis],
      };

      setAnimationSettingsState((prev) => ({
        ...prev,
        activeAxes: newActiveAxes,
      }));

      // Apply to selected effects
      applySettingsToSelectedEffects('activeAxes', newActiveAxes);
    },
    [
      animationSettings.activeAxes,
      setAnimationSettingsState,
      applySettingsToSelectedEffects,
    ]
  );

  // Toggle animation effects expanded
  const toggleAnimationEffects = useCallback(() => {
    // This would need to be handled by the parent component
    // For now, we'll just return a function that can be called
    return () => {
      // Toggle logic would go here
    };
  }, []);

  // Toggle animation settings expanded
  const toggleAnimationSettings = useCallback(() => {
    // This would need to be handled by the parent component
    // For now, we'll just return a function that can be called
    return () => {
      // Toggle logic would go here
    };
  }, []);

  return {
    loadSettingsFromSelectedEffect,
    applySettingsToSelectedEffects,
    updateSetting,
    updateSelectedEffectsProperty,
    updatePosition,
    toggleAxis,
    toggleAnimationEffects,
    toggleAnimationSettings,
  };
};

// Helper function for finding next available row
const findNextAvailableRow = (
  objectId,
  startTime,
  endTime,
  timelineEffects,
  excludeEffectId = null
) => {
  const objectEffects = timelineEffects.filter(
    (effect) => effect.objectId === objectId && effect.id !== excludeEffectId
  );

  const hasTimeOverlap = (start1, end1, start2, end2) => {
    return !(end1 <= start2 || start1 >= end2);
  };

  for (let row = 0; row < 20; row++) {
    const effectsInRow = objectEffects.filter(
      (effect) => (effect.row || 0) === row
    );
    const hasOverlap = effectsInRow.some((effect) => {
      return hasTimeOverlap(
        startTime,
        endTime,
        effect.startTime,
        effect.endTime
      );
    });

    if (!hasOverlap) {
      return row;
    }
  }

  return objectEffects.length;
};
